import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';

export default function PackingPrint() {
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get('orderNumber') || searchParams.get('order') || '';
  const [step, setStep] = useState('starting');
  const [error, setError] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!orderNumber) {
      setError('Missing orderNumber URL parameter.');
      setStep('error');
      return;
    }

    (async () => {
      try {
        const orderNumberClean = orderNumber.startsWith('#') ? orderNumber.substring(1) : orderNumber;
        const encoded = encodeURIComponent(orderNumberClean);

        setStep('fetching');
        const orderResp = await axiosInstance.get(`api/orders/${encoded}/`);
        const orderData = orderResp.data;
        if (!orderData || !orderData.products) {
          throw new Error('Order has no products.');
        }

        const allHaveDims = orderData.products.every(p => p.dimensions);
        if (!allHaveDims) {
          throw new Error('Some products are missing dimensions — open the order in the simulator first to apply fallbacks.');
        }

        setStep('calculating');
        const shippingCountry = orderData.shipping_address?.country || '';
        const postalCode = orderData.shipping_address?.zip || '';
        const calcResp = await axiosInstance.post(`api/orders/${encoded}/`, {
          products: orderData.products,
          shipping_country: shippingCountry,
          postal_code: postalCode,
        });
        if (calcResp.data.status !== 'success') {
          throw new Error(calcResp.data.message || 'Packing calculation failed.');
        }
        const solution = calcResp.data.solution;
        const alternativeSolutions = calcResp.data.alternative_solutions || [];

        setStep('generating');
        const pdfResp = await axiosInstance.post(
          `api/orders/${encoded}/pdf/`,
          { solution, alternative_solutions: alternativeSolutions, order_data: orderData },
          { responseType: 'blob' }
        );

        const blob = new Blob([pdfResp.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `packing_report_${orderNumberClean}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setStep('done');
        setTimeout(() => { try { window.close(); } catch {} }, 1500);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to generate PDF.');
        setStep('error');
      }
    })();
  }, [orderNumber]);

  const stepLabel = {
    starting: 'Starting…',
    fetching: 'Fetching order…',
    calculating: 'Calculating optimal packing…',
    generating: 'Generating PDF…',
    done: 'PDF downloaded — closing tab',
    error: 'Error',
  }[step];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          {step === 'error' ? (
            <AlertCircle className="h-6 w-6 text-red-500" />
          ) : step === 'done' ? (
            <CheckCircle className="h-6 w-6 text-emerald-500" />
          ) : (
            <Loader2 className="h-6 w-6 text-primary animate-spin" />
          )}
          <h1 className="text-lg font-semibold">Packing Report — {orderNumber || '?'}</h1>
        </div>
        <p className="text-sm text-muted-foreground">{stepLabel}</p>
        {error && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-3 py-2 text-sm text-red-800 dark:text-red-200">
            {error}
          </div>
        )}
        {step === 'done' && (
          <p className="text-xs text-muted-foreground mt-3">
            If the download didn't start, check your browser's pop-up settings.
          </p>
        )}
      </div>
    </div>
  );
}
